<template>
  <v-popover
    class='mui-popover'
    :offset='offset'
    :popoverClass='popoverClasses'
    :placement='placement'
    :open.sync='open'
  >
    <slot></slot>

    <template slot="popover">
      <button
        class="mui-popover__close-button"
        v-close-popover v-if='hasCloseButton'
      >
        <ion-icon
          class='icon'
          name='close'
        ></ion-icon>
      </button>

      <slot
        name='content'
      ></slot>

    </template>

  </v-popover>
</template>

<script>
export default {
  name: 'mui-popover',

  props: {

		/**
		 * The popover placement related to the target element.
		 */
		placement: {
			type: String,
			default: 'bottom',
			validator: (value) => [
				'top',
				'right',
				'bottom',
				'left'
			].includes(value)
		},

		/**
		 * Offset of the position (px), related to the trigger element
		 * Values must be described in X,Y coordinates
		 */
		offset: {
			type: String,
			default: '8'
		},

		/**
		 * The size is related to the max width of the the popover
		 */
		size: {
			type: String,
			default: 'small',
			validator: (value) => [
				'small',
				'medium',
				'large'
			].includes(value)
		},

		/**
		 * Controls externally whether the popover should be open or not
		 */
		isOpen: {
			type: Boolean,
			default: false
		},

		/**
		 * Controls whether the popover should have a default close button
		 */
		hasCloseButton: {
			type: Boolean,
			default: false
		}
	},

	computed: {

		/**
		 * Builds the popover size class name based on the size prop
		 * @returns {string}
		 */
		sizeClass() {
			return `mui-popover--${this.size}`;
		},

		/**
		 * Builds the popover style class according to the variables available
		 * @returns {string}
		 */
		popoverClasses() {
			return `mui-popover ${this.sizeClass}`;
		}
	},

	data() {
		return {open: false};
	},

	watch: {

		/**
		 * Emits to parent the value of variable "open"
		 * @param {boolean} value
		 */
		open(value) {
			this.$emit('update:open', value);
		},

		/**
		 * Alternate the value of open when the prop isOpen changes
		 * @param {boolean} value
		 */
		isOpen(value) {
			this.open = value;
		}
	}
}
</script>

<style lang="stylus" scoped>
.mui-popover
  display block !important
  z-index 10000
  position relative

  &--small
    max-width 496px

  &--medium
    max-width 656px

  &--large
    max-width 816px

  &__close-button
    display flex
    padding 2px
    border-radius 50%
    cursor pointer
    position absolute
    right 8px
    top 4px
    transition all .25s ease-in-out
    background-color transparent
    border none

    &:hover
      background-color #f2f6f9
      color #00519D

  .trigger
    display flex !important
    align-items center
    justify-content center

  .popover-inner
    padding 8px 16px
    border-radius 4px
    background-color #fff !important
    box-shadow 0 2px 5px rgba(#26303C, .2) !important
    color #3B3F51 !important

  /*
  * Default styles needed to ensure the library (v-tooltip) works correctly
  */
  .v-tooltip-arrow
    width 0
    height 0
    border-style solid
    position absolute
    margin 5px
    border-color #00254D !important
    z-index 1

  &[x-placement^="top"]
    margin-bottom 5px

    .v-tooltip-arrow
      border-width 5px 5px 0 5px
      border-left-color transparent !important
      border-right-color transparent !important
      border-bottom-color transparent !important
      bottom -5px
      left calc(50% - 5px)
      margin-top 0
      margin-bottom 0


  &[x-placement^="bottom"]
    margin-top 5px

    .v-tooltip-arrow
      border-width 0 5px 5px 5px
      border-left-color transparent !important
      border-right-color transparent !important
      border-top-color transparent !important
      top -5px
      left calc(50% - 5px)
      margin-top 0
      margin-bottom 0

  &[x-placement^="right"]
    margin-left 10px !important

    .v-tooltip-arrow
      border-width 10px 10px 10px 0 !important
      border-left-color transparent !important
      border-top-color transparent !important
      border-bottom-color transparent !important
      left -10px !important
      top calc(50% - 15px) !important
      margin-left 0
      margin-right 0

  &[x-placement^="left"]
    margin-right 5px

    .v-tooltip-arrow
      border-width 5px 0 5px 5px
      border-top-color transparent !important
      border-right-color transparent !important
      border-bottom-color transparent !important
      right -5px
      top calc(50% - 5px)
      margin-left 0
      margin-right 0

  .popover-arrow
    border-color #fff !important

</style>
